import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { SchoolLocationAutofill } from 'features/schools/SchoolLocationAutofill';
import {
  COORDINATE_PAIR_ERROR,
  isEmptyLocationValue,
  missingLocationFields,
  parseCoordinatePair,
} from 'utils/schoolLocation';
import { isValidCoordinates } from 'utils/timezones';

import FieldsContainer from '../../Fields/FieldsContainer';
import ModalLoading from '../ModalLoading/ModalLoading';
import ModalSuccess from '../ModalSuccess/ModalSuccess';
import './StepContainer.scss';

const StepContainer = ({
  modalData: {
    steps: initialSteps,
    onSubmit,
    getSuccessText,
    getErrorText,
    isConfirmationDialog,
  },
  close,
}) => {
  const [index, setIndex] = useState(0);
  const [steps, setSteps] = useState(initialSteps);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [form, setForm] = useState({});
  const [coordinatesPasted, setCoordinatesPasted] = useState(false);
  const locationRequest = useRef(0);
  const addressTimer = useRef();
  const initialFields = initialSteps.flatMap((step) => step.fields);
  const initialValues = Object.fromEntries(
    initialFields.map((field) => [field.prop, field.defaultValue]),
  );
  const countryIsDefault = useRef(
    initialFields.some(
      (field) => field.prop === 'country' && field.autofillDefault,
    ),
  );
  const locationValues = { ...initialValues, ...form };
  const hasCoordinates =
    steps[index].fields.some((field) => field.prop === 'lat') &&
    steps[index].fields.some((field) => field.prop === 'lng');

  const pasteCoordinates = (text) => {
    const coordinates = parseCoordinatePair(text);
    if (!coordinates) {
      setSubmissionError(COORDINATE_PAIR_ERROR);
      return;
    }
    locationRequest.current += 1;
    clearTimeout(addressTimer.current);
    setSubmissionError('');
    setCoordinatesPasted(true);
    setForm((current) => ({ ...current, ...coordinates }));
  };

  useEffect(
    () => () => {
      locationRequest.current += 1;
      clearTimeout(addressTimer.current);
    },
    [],
  );

  useEffect(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => ({
        ...step,
        fields: step.fields.map((field) => {
          if (index !== i) return field;
          const value = form[field.prop];
          if (value === undefined) {
            return field;
          }

          return {
            ...field,
            currentValue: value,
            valid: field.validators
              ? field.validators.every((validate) => validate(value))
              : true,
          };
        }),
      })),
    );
  }, [index, form]);

  const isVisible = (field) =>
    typeof field.isVisible === 'function'
      ? field.isVisible(form)
      : field.isVisible !== false;
  const isValid = steps[index].fields.filter(isVisible).every((f) => f.valid);
  const isFirstStep = index === 0;
  const isLastStep = index === steps.length - 1;

  const onPrevious = () => (isFirstStep ? close() : setIndex((i) => i - 1));

  const onNext = () => {
    if (!isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, i) =>
          index !== i
            ? step
            : {
                ...step,
                fields: step.fields.map(blurField),
              },
        ),
      );
    } else if (isLastStep) {
      submitForm();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const blurField = (field) => ({
    ...field,
    blurred: true,
    ...(field.subFields && {
      subFields: field.subFields.map((sf) => ({
        ...sf,
        blurred: true,
      })),
    }),
  });

  const submitForm = () => {
    setSubmissionError('');
    setIsLoading(true);

    onSubmit(form)
      .then((result) => {
        setIsLoading(false);
        setIsSuccess(true);
        setSubmissionMessage(getSuccessText(result));
      })
      .catch((error) => {
        setIsLoading(false);
        setIsSuccess(false);
        setSubmissionError(getErrorText(error));
      });
  };

  const updateField = (value, field, isUserEdit = false) => {
    if (isUserEdit && field.prop === 'country')
      countryIsDefault.current = false;

    switch (field.type) {
      case 'GoogleAutoCompleteField': {
        // Reverse geocoding fills the address without changing the coordinates.
        if (
          value === locationValues.address &&
          isValidCoordinates(form.lat, form.lng)
        )
          return;
        const request = ++locationRequest.current;
        clearTimeout(addressTimer.current);
        // An edited address invalidates the previous location immediately.
        setForm((f) => ({
          ...f,
          [field.prop]: value,
          lat: '',
          lng: '',
          timezone: '',
        }));
        if (field.blurred && value.trim()) {
          addressTimer.current = setTimeout(() => {
            geocodeByAddress(value)
              .then((results) => getLatLng(results[0]))
              .then(({ lat, lng }) => {
                if (request === locationRequest.current)
                  setForm((f) => ({ ...f, lat, lng }));
              })
              .catch(() => {
                if (request === locationRequest.current)
                  setSubmissionError(
                    'Could not find that address. Check the address or enter the school coordinates.',
                  );
              });
          }, 400);
        }
        break;
      }
      default:
        if (field.prop === 'lat' || field.prop === 'lng') {
          locationRequest.current += 1;
          clearTimeout(addressTimer.current);
        }
        setForm((f) => {
          const nextForm = {
            ...f,
            ...(f[field.prop] !== value ? field.resetFields : {}),
            [field.prop]: value,
          };
          return nextForm;
        });
    }
  };

  if (isLoading) {
    return <ModalLoading />;
  }

  if (submissionMessage) {
    return (
      <ModalSuccess
        isSuccess={isSuccess}
        close={close}
        message={submissionMessage}
      />
    );
  }

  return (
    <div className="step-container">
      <Modal.Header closeButton>
        <Modal.Title>{steps[index].name}</Modal.Title>
      </Modal.Header>

      <div className="step">
        <div className="current-step">
          {submissionError && <Alert variant="danger">{submissionError}</Alert>}
          <FieldsContainer
            fields={steps[index].fields.filter(isVisible)}
            updateParent={updateField}
            form={form}
            onCoordinatePaste={pasteCoordinates}
            autofillTimezoneOnlyIfEmpty={coordinatesPasted}
          />
          {hasCoordinates && (
            <>
              <p className="text-muted">
                You can paste a latitude, longitude pair into either coordinate
                field.
              </p>
              <SchoolLocationAutofill
                latitude={form.lat}
                longitude={form.lng}
                enabled={
                  !isLoading &&
                  (isEmptyLocationValue(locationValues.country) ||
                    isEmptyLocationValue(locationValues.state) ||
                    ('address' in initialValues &&
                      isEmptyLocationValue(locationValues.address)))
                }
                onResolved={(location) => {
                  setForm((current) => {
                    const updates = missingLocationFields(
                      { ...initialValues, ...current },
                      location,
                      countryIsDefault.current,
                    );
                    if (updates.country) countryIsDefault.current = false;
                    return { ...current, ...updates };
                  });
                }}
              />
            </>
          )}
        </div>
      </div>

      <Modal.Footer>
        <Button className="cancel-btn" onClick={onPrevious}>
          {isFirstStep ? 'Cancel' : 'Previous'}
        </Button>

        <Button
          variant={!isValid ? 'secondary' : isLastStep ? 'success' : 'primary'}
          className={!isValid ? 'disabled' : isLastStep ? '' : 'next-btn'}
          onClick={onNext}
        >
          {isLastStep ? (isConfirmationDialog ? 'Confirm' : 'Save') : 'Next'}
        </Button>
      </Modal.Footer>
    </div>
  );
};

export default StepContainer;
